"use client";

import { motion } from "framer-motion";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type TabItem = {
  id: string;
  label: React.ReactNode;
};

interface AnimatedTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (id: string) => void;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  underlineColor?: string;
  overlay?: boolean;
}

const transition = {
  type: "tween",
  ease: "easeOut",
  duration: 0.15,
} as const;

export const AnimatedTabs = ({
  tabs,
  activeTab,
  onTabChange,
  className,
  tabClassName,
  activeTabClassName,
  underlineColor = "#0077FF",
  overlay = false,
}: AnimatedTabsProps) => {
  const [buttonRefs, setButtonRefs] = useState<Array<HTMLButtonElement | null>>(
    [],
  );
  const navRef = useRef<HTMLDivElement>(null);

  const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);

  useEffect(() => {
    setButtonRefs((prev) => prev.slice(0, tabs.length));
  }, [tabs.length]);

  const navRect = navRef.current?.getBoundingClientRect();
  const selectedRect = buttonRefs[currentIndex]?.getBoundingClientRect();

  return (
    <div className={cn("relative border-b border-gray-200", className)}>
      {overlay && (
        <>
          <div className="absolute left-0 top-0 w-10 h-full bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 w-10 h-full bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        </>
      )}
      <div className="max-w-full overflow-x-auto scrollbar-none">
        <nav ref={navRef} className="inline-flex gap-1.5 relative">
          {tabs.map((tab, i) => {
            const isActive = activeTab === tab.id;

            return (
              <motion.button
                key={tab.id}
                ref={(el) => {
                  buttonRefs[i] = el;
                }}
                onClick={() => onTabChange(tab.id)}
                style={{
                  transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                }}
                className={cn(
                  "cursor-pointer whitespace-nowrap first:px-0 p-2 text-sm relative",
                  "transition-all duration-200",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0",
                  isActive ? "text-[#0077FF]" : "text-muted-foreground",
                  tabClassName,
                  isActive && activeTabClassName,
                )}
              >
                {tab.label}
              </motion.button>
            );
          })}

          {selectedRect && navRect && (
            <motion.div
              className="absolute z-10 bottom-[-1px] h-0.5"
              style={{ backgroundColor: underlineColor }}
              initial={false}
              animate={{
                width: selectedRect.width,
                x: selectedRect.left - navRect.left,
                opacity: 1,
              }}
              transition={transition}
            />
          )}
        </nav>
      </div>
    </div>
  );
};
